import React, { useState } from 'react';
import Card from '../Common/Card';
import Button from '../Common/Button';
import { useWeb3 } from '../../context/Web3Context';
import { notify, removeNotify } from '../../utils/notify';
import { parseContractError } from '../../utils/errors';

const PRESETS = [
  { label: '1 Min',  seconds: 60,    desc: 'Demo speed' },
  { label: '5 Min',  seconds: 300,   desc: 'Fast test' },
  { label: '1 Hour', seconds: 3600,  desc: 'Hourly ROI' },
  { label: '1 Day',  seconds: 86400, desc: 'Production' }
];

const formatDuration = (seconds) => {
  const s = Number(seconds);
  if (!s) return '—';
  if (s >= 86400 && s % 86400 === 0) return `${s / 86400}d`;
  if (s >= 3600 && s % 3600 === 0) return `${s / 3600}h`;
  if (s >= 60 && s % 60 === 0) return `${s / 60}m`;
  return `${s}s`;
};

const ProtocolTiming = ({ currentSeconds, onChange }) => {
  const { contract } = useWeb3();
  const [unit, setUnit] = useState('seconds');
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState('');

  const submitSeconds = async (seconds, label) => {
    if (!contract) return;
    if (!seconds || seconds <= 0) {
      notify.error('Timeframe must be greater than 0');
      return;
    }
    setBusy(label);
    let lid = null;
    try {
      lid = notify.loading('Updating ROI timeframe...');
      const tx = await contract.setROITimeframe(seconds);
      await tx.wait();
      removeNotify(lid); lid = null;
      notify.success('ROI timeframe updated to ' + formatDuration(seconds));
      setValue('');
      onChange && onChange();
    } catch (err) {
      if (lid) removeNotify(lid);
      notify.error(parseContractError(err));
    } finally {
      setBusy('');
    }
  };

  const submitCustom = () => {
    const n = parseInt(value, 10);
    if (isNaN(n)) {
      notify.error('Enter a valid number');
      return;
    }
    const multipliers = { seconds: 1, minutes: 60, hours: 3600, days: 86400 };
    const seconds = n * multipliers[unit];
    submitSeconds(seconds, 'custom');
  };

  const current = Number(currentSeconds || 0);

  return (
    <Card
      title="ROI Timeframe"
      subtitle="Owner only · One period = how often ROI accrues"
    >
      <div style={{
        padding: 18,
        background: 'linear-gradient(135deg, rgba(255, 170, 0, 0.08), rgba(0, 212, 255, 0.05))',
        border: '1px solid rgba(255, 170, 0, 0.3)',
        borderRadius: 'var(--radius-md)',
        marginBottom: 20,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 16,
        flexWrap: 'wrap'
      }}>
        <div>
          <div className="stat-label">Current Timeframe</div>
          <div style={{
            fontFamily: 'JetBrains Mono',
            fontSize: 28,
            fontWeight: 700,
            color: 'var(--accent-orange)',
            textShadow: '0 0 20px rgba(255, 170, 0, 0.4)',
            marginTop: 4
          }}>
            {formatDuration(current)}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4, fontFamily: 'JetBrains Mono' }}>
            {current.toLocaleString()} seconds
          </div>
        </div>
        <div style={{
          fontSize: 11,
          color: 'var(--text-muted)',
          maxWidth: 240,
          lineHeight: 1.5,
          textAlign: 'right'
        }}>
          ROI compounds once per timeframe. Plan periods × this duration = total stake length.
        </div>
      </div>

      <div className="form-label" style={{ marginBottom: 10 }}>Quick Presets</div>
      <div className="grid grid-4" style={{ gap: 10, marginBottom: 24 }}>
        {PRESETS.map((p) => {
          const isActive = current === p.seconds;
          return (
            <button
              key={p.seconds}
              onClick={() => submitSeconds(p.seconds, `preset-${p.seconds}`)}
              disabled={busy === `preset-${p.seconds}` || isActive}
              style={{
                padding: '14px 10px',
                background: isActive ? 'rgba(0, 212, 255, 0.1)' : 'var(--bg-input)',
                border: '1px solid ' + (isActive ? 'var(--accent-cyan)' : 'var(--border-secondary)'),
                borderRadius: 'var(--radius-md)',
                cursor: isActive ? 'default' : 'pointer',
                opacity: busy === `preset-${p.seconds}` ? 0.6 : 1,
                color: 'var(--text-primary)',
                transition: 'all 0.2s ease',
                textAlign: 'center',
                boxShadow: isActive ? 'var(--glow-cyan)' : 'none'
              }}
              onMouseEnter={(e) => {
                if (!isActive && busy !== `preset-${p.seconds}`) {
                  e.currentTarget.style.borderColor = 'var(--accent-cyan)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive) e.currentTarget.style.borderColor = 'var(--border-secondary)';
              }}
            >
              <div style={{
                fontFamily: 'Orbitron, sans-serif',
                fontWeight: 800,
                fontSize: 14,
                letterSpacing: '0.1em',
                color: isActive ? 'var(--accent-cyan)' : 'var(--text-primary)'
              }}>{p.label}</div>
              <div style={{
                fontSize: 10,
                color: 'var(--text-muted)',
                marginTop: 4,
                letterSpacing: '0.05em'
              }}>{p.desc}</div>
            </button>
          );
        })}
      </div>

      <div className="form-label">Custom Timeframe</div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          className="form-input"
          style={{ flex: 1 }}
          placeholder="Enter a number"
          value={value}
          onChange={(e) => setValue(e.target.value.replace(/[^0-9]/g, ''))}
        />
        <select
          className="form-input"
          style={{ maxWidth: 140 }}
          value={unit}
          onChange={(e) => setUnit(e.target.value)}
        >
          <option value="seconds">Seconds</option>
          <option value="minutes">Minutes</option>
          <option value="hours">Hours</option>
          <option value="days">Days</option>
        </select>
        <Button
          variant="primary"
          loading={busy === 'custom'}
          onClick={submitCustom}
          disabled={!value}
        >Apply</Button>
      </div>
      <div className="form-helper" style={{ marginTop: 8 }}>
        Use 1 Day for production. Shorter values are for demos/testing only.
      </div>
    </Card>
  );
};

export default ProtocolTiming;
